<template>
  <v-dialog v-model="dialog" max-width="600" @click:outside="cancel">
    <v-card flat v-if="dialog">
      <v-card-title>
        Add People
        <v-spacer></v-spacer>
        <v-btn icon @click="cancel">
          <v-icon>close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-subtitle>
        Who would you like to participate in this meeting space?
      </v-card-subtitle>
      <v-form ref="form" v-model="valid">
        <v-card-text class="pt-0">
          <v-row dense>
            <v-col cols="12">
              <member-autocomplete
                v-model="newAttendees"
                :multiple="false"
                v-on:change="addAttendee"
                clearOnSelection
                allowAll
                :memberIdsToNotShow="attendeeIds"
                hide-details
                placeholder="Search to add members from your company"
                label=""
              ></member-autocomplete>
            </v-col>
            <v-col cols="12">
              <v-icon class="mr-2" small>info</v-icon>
              All users will be able to
              <span class="text-decoration-underline">view and contribute</span>
              to this meeting
            </v-col>
          </v-row>
          <div class="scroll-div mr-n4 pt-4">
            <template v-for="(attendee, index) in sorted_attendees">
              <v-row :key="attendee.id">
                <v-col cols="auto" class="my-auto">
                  <user-avatar :size="35" :user="attendee"></user-avatar>
                </v-col>
                <v-col class="my-auto">
                  <div>
                    {{ attendee.fullName }}
                    <v-tooltip right>
                      <template v-slot:activator="{ on, attrs }">
                        <v-chip
                          small
                          v-bind="attrs"
                          v-on="on"
                          :class="{
                            'accent--text': true,
                            'ml-1': true,
                            'd-none': !ownerIds.includes(attendee.id)
                          }"
                          color="background"
                        >
                          Owner
                        </v-chip>
                      </template>
                      <div class="tooltip-limit-width">
                        <span class="accent--text font-italic">Owners</span> can
                        invite and remove individuals as well as start/end
                        meetings
                      </div>
                    </v-tooltip>
                  </div>
                  <div class="caption">
                    {{ attendee.jobTitle }}
                  </div>
                </v-col>
                <v-spacer></v-spacer>
                <v-col cols="auto" class="my-auto">
                  <v-menu absolute>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        icon
                        :disabled="
                          attendee.id == checkin.createdBy ||
                            (!allowEdit && attendee.id != profile.id)
                        "
                      >
                        <v-icon>more_vert</v-icon>
                      </v-btn>
                    </template>
                    <v-list>
                      <v-list-item @click="remove(attendee.id)">
                        <v-list-item-title>Remove</v-list-item-title>
                      </v-list-item>
                      <v-list-item
                        @click="addOwner(attendee.id)"
                        v-if="!ownerIds.includes(attendee.id) && allowEdit"
                      >
                        <v-list-item-title>Make Owner</v-list-item-title>
                      </v-list-item>
                      <v-list-item
                        @click="removeOwner(attendee.id)"
                        v-if="ownerIds.includes(attendee.id) && allowEdit"
                      >
                        <v-list-item-title>Remove as Owner</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </v-col>
              </v-row>
              <v-divider
                :key="attendee.id + 'divider'"
                v-if="index < sorted_attendees.length - 1"
              ></v-divider>
            </template>
          </div>
          <v-row dense class="pt-4">
            <v-col cols="auto">
              <v-btn color="performance" :loading="saving" @click="save">
                Save
              </v-btn>
              <span class="font-italic ml-4">
                Participants won't be notified
              </span>
            </v-col>
          </v-row>
        </v-card-text>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import MemberAutocomplete from "@/components/members/MemberAutocomplete";
export default {
  name: "EditCheckInAttendeesDialog",
  components: { MemberAutocomplete },
  props: { allowEdit: { type: Boolean } },
  data: () => ({
    dialog: false,
    valid: false,
    saving: false,
    checkin: {},
    newAttendees: [],
    ownerIds: []
  }),
  watch: {
    checkin(val, oldVal) {
      if (val != oldVal) {
        val.attendees = val.attendees.map(x => {
          x.isActive = true;
          return x;
        });
      }
    }
  },
  computed: {
    sorted_attendees() {
      let self = this;
      return this._.sortBy(this.checkin.attendees, function(o) {
        return o.id != self.checkin.createdBy;
      });
    },
    attendeeIds() {
      return this.checkin.attendees.map(x => x.id);
    },
    ...mapGetters({
      search_users: "user/search_users",
      profile: "profile/profile"
    })
  },
  methods: {
    addAttendee(val) {
      if (val && val.length) {
        let attendee = this.search_users.find(x => x.id == val[0]);
        if (attendee) {
          let index = this.checkin.attendees.findIndex(
            x => x.id == attendee.id
          );
          if (index >= 0) {
            this.checkin.attendees[index].isActive = true;
          } else {
            this.checkin.attendees.push(
              Object.assign({ isActive: true }, attendee)
            );
          }
        }
      }
    },
    cancel() {
      this.dialog = false;
      this.$emit("cancel");
    },
    save() {
      if (this.$refs.form.validate()) {
        this.checkin.attendees = this.checkin.attendees.filter(x => x.isActive);
        this.checkin.ownerIds = this.ownerIds;
        if (this.allowEdit) {
          this.saving = true;
          this.$store
            .dispatch("checkin/saveCheckIn", this.checkin)
            .then(() => {
              this.finishDialog("Meeting attendees saved");
            })
            .catch(error => {
              this.logError(error);
              this.$root.$alert(
                "Error Occurred",
                "An error occurred while saving your meeting attendees."
              );
            })
            .finally(() => {
              this.saving = false;
            });
        } else if (!this.checkin.attendees.find(x => x.id == this.profile.id)) {
          this.saving = true;
          this.$store
            .dispatch("checkin/leaveCheckIn", this.checkin.definitionId)
            .then(() => {
              this.finishDialog("Meeting left");
              this.$router.push({ path: "/features/meetings" });
            })
            .catch(error => {
              this.logError(error);
              this.$root.$alert(
                "Error Occurred",
                "An error occurred while saving your meeting attendees."
              );
            })
            .finally(() => {
              this.saving = false;
            });
        } else {
          this.finishDialog();
        }
      }
    },
    showDialog(checkin) {
      this.dialog = true;
      this.checkin = Object.assign({ attendees: [] }, checkin);
      this.ownerIds = checkin.ownerIds || [];
    },
    finishDialog(text) {
      this.$emit("save");
      if (text) {
        this.$root.$snackbarSuccess(text);
      }
      this.dialog = false;
    },
    remove(id) {
      let index = this.checkin.attendees.findIndex(x => x.id == id);
      if (index >= 0) {
        this.checkin.attendees.splice(index, 1);
      }
    },
    removeOwner(id) {
      let index = this.ownerIds.findIndex(x => x == id);
      if (index >= 0) {
        this.ownerIds.splice(index, 1);
      }
    },
    addOwner(id) {
      let index = this.ownerIds.findIndex(x => x == id);
      if (index < 0) {
        this.ownerIds.splice(0, 0, id);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.scroll-div {
  overflow-y: scroll;
  max-height: 350px;
  overflow-x: hidden;
}
</style>
